import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import type { ToolEntry } from '~/components/toolbar/shared/toolEntry';
import type { ToolManager } from '~/engine/tool/ToolManager';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { isDialogOpen, isTextEntryElement } from '~/utils/textEntry';

/**
 * Whether keys reach the viewer: no dialog, modal or menu is open
 * ({@link isDialogOpen}), and an image tile is on screen.
 */
function keysReachViewer(): boolean {
    if (isDialogOpen()) {
        return false;
    }
    return Array.from(
        document.querySelectorAll('[data-testid="viewport-tile"]')
    ).some((tile) => tile.getClientRects().length > 0);
}

/**
 * Whether a plain key press may act on the viewer: no modifier, not an
 * auto-repeat, focus outside a text field, keys that reach the viewer
 * ({@link keysReachViewer}), and no tool action in progress, such as a
 * half-drawn line.
 */
export function keyMayActOnViewer(
    event: KeyboardEvent,
    toolManager: ToolManager | undefined
): boolean {
    return (
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey &&
        !event.repeat &&
        !isTextEntryElement(document.activeElement) &&
        keysReachViewer() &&
        !toolManager?.isActiveToolActionInProgress()
    );
}

/**
 * Arms an entry when its key is pressed; a disabled entry warns instead
 * (see {@link ToolEntry.arm}). A key that may not act on the viewer
 * ({@link keyMayActOnViewer}) is left alone, so other handlers and the
 * browser still see it.
 */
export function useToolHotkeys(entries: readonly ToolEntry[]): void {
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;
    // Read at key time, so the listener does not resubscribe on every render.
    const entriesRef = useRef(entries);
    entriesRef.current = entries;

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            const key = event.key.toLowerCase();
            const entry = entriesRef.current.find((e) => e.hotkey === key);
            if (entry && keyMayActOnViewer(event, toolManager)) {
                entry.arm();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [toolManager]);
}
